/**
 * Реестр пресетов площадок (ADR 0007). Единственное место, где живут строки
 * `venue.type` — код читает пресет, а не сравнивает тип в бизнес-логике.
 * Новый тип площадки = запись здесь + данные venue, без правок движка.
 * В v1 пресеты уедут во внешние версионируемые профили (ADR 0011).
 */

export interface VenueCapabilities {
  /** Многоуровневая (этажи/подземный паркинг) — включает тоггл «Объём». */
  readonly multiLevel: boolean
  /** Изометрический показ уместен (закрытая площадка со зданиями). */
  readonly iso: boolean
  /** Есть вертикальный транспорт (лифт/эскалатор/лестница) — выбор подъёма. */
  readonly verticalTransport: boolean
  /** Есть автопарковка — фича «где моя машина». */
  readonly parking: boolean
  /** Уличная площадка (промзона) — маршрутизация транспорта, без этажей. */
  readonly outdoor: boolean
  /** Аварийный слой (эвакуация/укрытия) подключается по профилю resilience. */
  readonly emergency: boolean
}

/** Картографическая палитра типа площадки (дизайн-система, ADR 0012). */
export interface MapPalette {
  readonly unit: string
  readonly unitLine: string
  readonly mapBg: string
}

export interface Preset {
  /** Категории POI для чипов быстрых целей (по порядку). */
  readonly chips: readonly string[]
  readonly capabilities: VenueCapabilities
  /** Отличная от дефолтной палитра карты (промзона холоднее и контрастнее). */
  readonly mapPalette?: MapPalette
}

const CAP_DEFAULT: VenueCapabilities = {
  multiLevel: false,
  iso: false,
  verticalTransport: false,
  parking: false,
  outdoor: false,
  emergency: false,
}

/** Явный дефолт для неизвестного типа — осмысленные цели, НЕ промзонный хардкод. */
export const GENERIC: Preset = {
  chips: ['entrance', 'wc', 'info'],
  capabilities: CAP_DEFAULT,
}

const PRESETS: Readonly<Record<string, Preset>> = {
  industrial_park: {
    chips: ['checkpoint', 'office', 'truck_scale'],
    capabilities: { ...CAP_DEFAULT, outdoor: true, parking: true },
    mapPalette: { unit: '#CDD3DD', unitLine: '#B9C0CC', mapBg: '#E8EAEF' },
  },
  mall: {
    chips: ['wc', 'info', 'entrance'],
    capabilities: {
      ...CAP_DEFAULT,
      multiLevel: true,
      iso: true,
      verticalTransport: true,
      parking: true,
    },
  },
  // ── Новые типы (BACKLOG №17, VENUE_TYPES.md): данные, без правок движка ──
  business_center: {
    chips: ['entrance', 'info', 'parking'],
    capabilities: {
      ...CAP_DEFAULT,
      multiLevel: true,
      iso: true,
      verticalTransport: true,
      parking: true,
    },
  },
  station: {
    chips: ['entrance', 'info', 'wc'],
    capabilities: { ...CAP_DEFAULT, multiLevel: true, verticalTransport: true, emergency: true },
    mapPalette: { unit: '#D8D2C4', unitLine: '#C4BCA8', mapBg: '#EFEBE2' },
  },
  expo: {
    chips: ['entrance', 'info', 'wc'],
    capabilities: { ...CAP_DEFAULT, iso: true, parking: true },
  },
  // Флагманские гипермаркеты Таллинна двухэтажны (еда внизу, промтовары
  // наверху) — мультиэтаж и вертикальный транспорт входят в пресет.
  hypermarket: {
    chips: ['entrance', 'checkout', 'wc'],
    capabilities: {
      ...CAP_DEFAULT,
      multiLevel: true,
      verticalTransport: true,
      iso: true,
      parking: true,
      emergency: true,
    },
    mapPalette: { unit: '#D3DCD2', unitLine: '#BFCBBE', mapBg: '#E9EFE8' },
  },
  // ── Вертикали фазы S (запрос владельца 27.07) ────────────────────────────
  airport: {
    chips: ['gate', 'check_in', 'security', 'baggage', 'wc'],
    capabilities: {
      ...CAP_DEFAULT,
      multiLevel: true,
      verticalTransport: true,
      parking: true,
      emergency: true,
      iso: true,
    },
    mapPalette: { unit: '#D6DEE8', unitLine: '#BFCAD8', mapBg: '#EAEFF5' },
  },
  port: {
    chips: ['terminal', 'berth', 'check_in', 'wc'],
    capabilities: {
      ...CAP_DEFAULT,
      multiLevel: true,
      verticalTransport: true,
      parking: true,
      outdoor: true,
      emergency: true,
      iso: true,
    },
    mapPalette: { unit: '#CFDCE4', unitLine: '#B6C7D2', mapBg: '#E6EFF4' },
  },
  // Судно: карта «едет» вместе с объектом — палубы вместо этажей, снаружи моря
  // нет смысла рисовать дороги (см. фазу S5).
  ship: {
    chips: ['deck', 'cabin', 'restaurant', 'muster', 'wc'],
    capabilities: {
      ...CAP_DEFAULT,
      multiLevel: true,
      verticalTransport: true,
      emergency: true,
      iso: true,
    },
    mapPalette: { unit: '#DCE6EE', unitLine: '#C3D2DE', mapBg: '#E9F1F6' },
  },
}

/** Пресет по типу площадки с явным generic-дефолтом (ADR 0007). */
export function resolvePreset(type: string): Preset {
  return PRESETS[type] ?? GENERIC
}

// ── Версионируемые профили (BACKLOG №16, ADR 0011) ───────────────────────────

/**
 * Реестр версий профилей: `mall@2` — точная версия, `mall` — последняя.
 * Профиль — Partial<Preset>: стек в venue.profiles сливается по порядку
 * (каждый следующий ДОПОЛНЯЕТ/переопределяет предыдущий, capabilities —
 * поле-в-поле). Новый профиль/версия = запись здесь, движок не меняется.
 */
/** Слой профиля: любые поля частично (capabilities — поле-в-поле). */
export interface ProfileLayer {
  readonly chips?: readonly string[]
  readonly capabilities?: Partial<VenueCapabilities>
  readonly mapPalette?: MapPalette
}

const PROFILE_VERSIONS: Readonly<Record<string, ProfileLayer>> = {
  'industrial_park@1': PRESETS['industrial_park'] ?? {},
  'mall@1': PRESETS['mall'] ?? {},
  'business_center@1': PRESETS['business_center'] ?? {},
  'station@1': PRESETS['station'] ?? {},
  'expo@1': PRESETS['expo'] ?? {},
  'hypermarket@1': PRESETS['hypermarket'] ?? {},
  // Сателлитный трек Resilience: добавляет аварийный слой любому типу.
  'resilience@1': { capabilities: { emergency: true } },
}

/** Последняя версия безверсионного имени ('mall' → 'mall@N' с макс. N). */
function latestVersion(name: string): ProfileLayer | undefined {
  if (PROFILE_VERSIONS[name]) return PROFILE_VERSIONS[name]
  let best: { v: number; p: ProfileLayer } | undefined
  for (const [key, p] of Object.entries(PROFILE_VERSIONS)) {
    const [base, ver] = key.split('@')
    if (base !== name) continue
    const v = Number(ver)
    if (!best || v > best.v) best = { v, p }
  }
  return best?.p
}

function mergePreset(acc: Preset, layer: ProfileLayer | undefined): Preset {
  if (!layer) return acc
  return {
    chips: layer.chips ?? acc.chips,
    capabilities: { ...acc.capabilities, ...layer.capabilities },
    ...((layer.mapPalette ?? acc.mapPalette)
      ? { mapPalette: layer.mapPalette ?? acc.mapPalette }
      : {}),
  }
}

/**
 * Пресет из стека профилей venue.profiles (порядок = приоритет применения);
 * без profiles — обратная совместимость через [type].
 */
export function resolveProfiles(meta: {
  readonly type: string
  readonly profiles?: readonly string[]
}): Preset {
  const names = meta.profiles ?? [meta.type]
  let acc = GENERIC
  for (const name of names) acc = mergePreset(acc, latestVersion(name))
  return acc
}
